<template>
  <v-container fluid>
    <v-skeleton-loader v-if="isLoading" type="table" />

    <v-row v-else>
      <v-col cols="12">
        <div>
          <v-row justify="center" class="mb-4">
            <v-tabs
              background-color="transparent"
              v-model="tab"
              centered
              class="mx-auto"
              slider-color="#185EA9"
              height="46"
            >
              <v-tab>{{
                $t("wkt.admin.vacation-policy.tab-general.title")
              }}</v-tab>
              <v-tab>{{
                $t("wkt.admin.vacation-policy.tab-policies.title")
              }}</v-tab>
            </v-tabs>
          </v-row>

          <v-tabs-items v-model="tab">
            <v-tab-item eager class="scrollTab">
              <v-row>
                <v-col cols="12">
                  <div class="mb-4">
                    <span style="font-size: 16px; font-weight: bold;">
                      {{
                        $t(
                          "wkt.admin.vacation-policy.vacation_accrual_date.title",
                        )
                      }}
                    </span>
                  </div>

                  <v-row align="center" class="mb-4">
                    <v-col cols="12" md="auto" class="py-0"> </v-col>
                    {{ formattedAccrualDate }}
                    <v-col cols="12" md="auto" class="py-0">
                      <v-btn
                        text
                        small
                        color="#185EA9"
                        :disabled="
                          !canModify || licenseType !== licenseTypeCorporate
                        "
                        style="text-decoration: underline; text-underline-offset: 2px;"
                        @click="openAccrualDateDialog"
                      >
                        <span>
                          {{
                            $t(
                              "wkt.admin.vacation-policy.vacation_accrual_date.change_date",
                            ).toUpperCase()
                          }}
                        </span>
                      </v-btn>
                    </v-col>
                    <v-col
                      v-if="licenseType !== licenseTypeCorporate"
                      cols="12"
                      md="auto"
                      class="py-0 grey--text text--darken-1"
                    >
                      <span>
                        ({{
                          $t(
                            "wkt.admin.vacation-policy.general.accrual_date.corporate_only",
                          )
                        }})
                      </span>
                    </v-col>
                  </v-row>

                  <div class="mb-4">
                    <span style="font-size: 16px; font-weight: bold;">
                      {{
                        $t(
                          "wkt.admin.general-customize.tab-advanced.section.default-vacation-policy",
                        )
                      }}
                    </span>
                  </div>

                  <v-row align="end" class="mb-2">
                    <v-col cols="12" md="5">
                      <v-text-field
                        :value="defaultVacationCountModeText"
                        :label="
                          $t(
                            'wkt.admin.vacation-policy.default-policy.count_mode',
                          )
                        "
                        dense
                        disabled
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        :value="
                          formatMetricValue(
                            company ? company.defaultYearlyVacationDays : 0,
                          )
                        "
                        :label="
                          $t(
                            'wkt.admin.vacation-policy.vacation-policy-window.yearly_vacation_days',
                          )
                        "
                        dense
                        disabled
                      />
                    </v-col>
                    <v-col cols="12" md="4" class="pb-md-6">
                      <v-btn
                        text
                        small
                        color="#185EA9"
                        :disabled="!canModify"
                        @click="openDefaultDialog"
                      >
                        <v-icon left small color="#185EA9">settings</v-icon>
                        <span
                          style="text-decoration: underline; text-underline-offset: 2px;"
                        >
                          {{
                            $t(
                              "wkt.admin.vacation-policy.general.table.configuration",
                            ).toUpperCase()
                          }}
                        </span>
                      </v-btn>
                    </v-col>
                  </v-row>

                  <v-row rows="12" class="mb-2" justify="center" align="center">
                      <v-alert
                        v-if="licenseAlertMessage"
                        dense
                        outlined
                        text
                        icon="warning"
                        type="warning"
                      >
                        <span style="color: black !important">{{ licenseAlertMessage }} </span>
                        <a href="#license">{{ $t("wkt.admin.vacation-policy.general.license_link") }}</a>
                      </v-alert>
                  </v-row>

                  <div class="mb-4">
                    <span style="font-size: 16px; font-weight: bold;">
                      {{
                        $t("wkt.admin.vacation-policy.general.additional_bags")
                      }}
                    </span>
                  </div>
                  <div>
                    <v-data-table
                      :headers="generalBagHeaders"
                      :items="generalBagRows"
                      :mobile-breakpoint="0"
                      disable-pagination
                      hide-default-footer
                      fixed-header
                      height="auto"
                      class="elevation-0 vacations-table"
                    >
                      <template v-slot:item="{ item }">
                        <tr>
                          <td>{{ item.orderLabel }}</td>
                          <td
                            :class="
                              !item.enabled ? 'grey--text text--lighten-1' : ''
                            "
                          >
                            {{ formatBagLabel(item) }}
                          </td>
                          <td
                            :class="
                              !item.enabled ? 'grey--text text--lighten-1' : ''
                            "
                          >
                            {{ formatBagUnit(item) }}
                          </td>
                          <td
                            :class="
                              !item.enabled ? 'grey--text text--lighten-1' : ''
                            "
                          >
                            {{
                              item.enabled
                                ? formatMetricValue(item.days)
                                : $t(
                                    "wkt.admin.vacation-policy.general.not_available",
                                  )
                            }}
                          </td>
                          <td
                            :class="
                              !item.enabled ? 'grey--text text--lighten-1' : ''
                            "
                          >
                            {{
                              formatBagBoolean(item.enabled, item.allowNegative)
                            }}
                          </td>
                          <td>{{ formatBoolean(item.enabled) }}</td>
                          <td class="text-right">
                            <v-btn
                              text
                              small
                              color="#185EA9"
                              :disabled="isBagActionDisabled(item.definition)"
                              @click.stop="openBagDialog(item.definition)"
                            >
                              <v-icon left small color="#185EA9"
                                >settings</v-icon
                              >
                              <span
                                style="text-decoration: underline; text-underline-offset: 2px;"
                              >
                                {{
                                  $t(
                                    "wkt.admin.vacation-policy.general.table.configuration",
                                  ).toUpperCase()
                                }}
                              </span>
                            </v-btn>
                          </td>
                        </tr>
                      </template>
                    </v-data-table>
                  </div>
                </v-col>
              </v-row>
            </v-tab-item>

            <v-tab-item eager>
              <v-row>
                <v-col cols="12">
                  <div>
                    <v-data-table
                      :headers="policyHeaders"
                      :items="policies"
                      :loading="apiLoading"
                      :loading-text="$t('wkt.admin.common.table.loading')"
                      :no-data-text="$t('wkt.admin.common.table.no_data')"
                      :no-results-text="$t('wkt.admin.common.table.no_results')"
                      :mobile-breakpoint="0"
                      disable-pagination
                      hide-default-footer
                      fixed-header
                      height="420"
                      class="elevation-0 vacations-table-additional"
                    >
                      <template v-slot:top>
                        <v-toolbar flat color="white" class="mb-3">
                          <v-row>
                            <v-col cols="2">
                              <v-text-field
                                v-model="search"
                                clearable
                                hide-details
                                class="vacation-policy-search"
                                :label="
                                  $t(
                                    'wkt.admin.vacation-policy.search_vacation_policy',
                                  )
                                "
                                @click:clear="onSearchUpdated"
                                @keyup="onSearchUpdated"
                              />
                            </v-col>
                          </v-row>
                        </v-toolbar>
                      </template>

                      <template v-slot:item="{ item, headers }">
                        <tr
                          style="cursor: pointer;"
                          @click="handlePolicyRowClick({ rawPolicy: item })"
                        >
                          <td
                            v-for="header in headers"
                            :key="`${item.id}-${header.value}`"
                          >
                            <template v-if="header.type === 'metric'">
                              <div class="vacation-policy-metric-cell">
                                <span
                                  class="vacation-policy-metric-cell__value"
                                  >{{
                                    formatMetricValue(item[header.value])
                                  }}</span
                                >
                                <span
                                  class="vacation-policy-metric-cell__unit"
                                  >{{ formatUnit(header.unit) }}</span
                                >
                              </div>
                            </template>
                            <template v-else-if="header.type === 'mode'">
                              {{ formatCountMode(item.assistedCountMode) }}
                            </template>
                            <template v-else>
                              {{ item.name }}
                            </template>
                          </td>
                        </tr>
                      </template>
                    </v-data-table>
                  </div>
                </v-col>
              </v-row>
            </v-tab-item>
          </v-tabs-items>
        </div>
      </v-col>
    </v-row>

    <v-tooltip v-if="!isLoading && tab === 1" left>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          dark
          fixed
          bottom
          right
          fab
          color="#185EA9"
          :disabled="!canModify"
          v-bind="attrs"
          v-on="on"
          @click="openPolicyEditor()"
        >
          <v-icon dark>add</v-icon>
        </v-btn>
      </template>
      <span>{{ $t("wkt.admin.vacation-policy.new_vacation_policy") }}</span>
    </v-tooltip>

    <VacationPolicyDefaultDialog ref="defaultDialog" />
    <VacationPolicyBagConfigDialog ref="bagDialog" />
    <VacationPolicyEditorDialog ref="policyDialog" />
    <VacationPolicyAccrualDateDialog ref="accrualDialog" />

    <v-snackbar v-model="showAlert" :timeout="alertTimeout" :color="alertColor">
      <span style="color: #ffffff;">{{ alertText }}</span>
      <v-btn text @click="showAlert = false">
        <span style="color: #ffffff;">Cerrar</span>
      </v-btn>
    </v-snackbar>
  </v-container>
</template>

<style scoped>

</style>

<script>
import gwt from "../../api/gwt";
import api from "../../api/worktime";
import BaseModule from "../common/BaseModule";
import deepClone from "../util/deep-clone";
import VacationPolicyAccrualDateDialog from "./dialogs/VacationPolicyAccrualDateDialog";
import VacationPolicyBagConfigDialog from "./dialogs/VacationPolicyBagConfigDialog";
import VacationPolicyDefaultDialog from "./dialogs/VacationPolicyDefaultDialog";
import VacationPolicyEditorDialog from "./dialogs/VacationPolicyEditorDialog";

const HOURS_BAG_UNIT_DAYS = 0;
const HOURS_BAG_UNIT_HOURS = 1;

const LICENSE_TYPE_FREE = 0;
const LICENSE_TYPE_BUSINESS = 1;
const LICENSE_TYPE_CORPORATE = 2;

export default {
  name: "VacationPolicyModule",
  mixins: [BaseModule],
  components: {
    VacationPolicyBagConfigDialog,
    VacationPolicyAccrualDateDialog,
    VacationPolicyDefaultDialog,
    VacationPolicyEditorDialog,
  },
  data() {
    return {
      tab: 0,
      isLoading: true,
      apiLoading: false,
      hoursBagUnitDays: HOURS_BAG_UNIT_DAYS,
      licenseTypeCorporate: LICENSE_TYPE_CORPORATE,
      company: null,
      sessionProfile: null,
      policies: [],
      search: "",
      searchTimer: null,
      showAlert: false,
      alertColor: "error",
      alertText: "",
      alertTimeout: 3000,
    };
  },
  computed: {
    canRead() {
      return this.sessionProfile
        ? this.matchRole(this.sessionProfile, ["vpolicy"])
        : false;
    },
    canModify() {
      return this.sessionProfile
        ? this.matchRoleModify(this.sessionProfile, ["vpolicy"])
        : false;
    },
    licenseType() {
      return this.company && this.company.licenseType != null
        ? Number(this.company.licenseType)
        : LICENSE_TYPE_FREE;
    },
    formattedAccrualDate() {
      return this.formatAccrualDate(
        this.company ? this.company.vacationDateLastInit : null,
      );
    },
    defaultVacationCountModeText() {
      return this.formatCountMode(
        this.company ? this.company.assistedVacationCountMode : 0,
      );
    },
    licenseAlertMessage() {
      if (!this.company) {
        return "";
      }

      if (this.licenseType === LICENSE_TYPE_FREE) {
        return this.$t(
          "wkt.admin.vacation-policy.tab-general.license_free_alert",
        );
      }

      if (this.licenseType === LICENSE_TYPE_BUSINESS) {
        return this.$t(
          "wkt.admin.vacation-policy.tab-general.license_businewss_alert",
        );
      }

      return "";
    },
    bagDefinitions() {
      return this.getBagDefinitions();
    },
    generalBagRows() {
      if (!this.company) {
        return [];
      }

      return this.bagDefinitions.map((definition, index) => ({
        orderLabel: this.getBagOrderLabel(index),
        enabled: Boolean(this.company[definition.enabledProp]),
        label: this.company[definition.labelProp],
        days: Number(this.company[definition.daysProp] || 0),
        unit: this.getDefinitionUnit(definition),
        allowNegative: Boolean(this.company[definition.allowNegativeProp]),
        definition,
      }));
    },
    generalBagHeaders() {
      return [
        {
          text: "",
          value: "orderLabel",
          sortable: false,
          width: "11%",
        },
        {
          text: this.$t("wkt.admin.vacation-policy.tab-general.bag_hours_name"),
          value: "label",
          sortable: false,
          width: "20%",
        },
        {
          text: this.$t("wkt.admin.vacation-policy.general.table.unit"),
          value: "unit",
          sortable: false,
          width: "12%",
        },
        {
          text: this.$t("wkt.admin.vacation-policy.general.table.annual_value"),
          value: "days",
          sortable: false,
          width: "15%",
        },
        {
          text: this.$t(
            "wkt.admin.vacation-policy.tab-general.bag_hours_allow_negative",
          ),
          value: "allowNegative",
          sortable: false,
          width: "16%",
        },
        {
          text: this.$t("wkt.admin.vacation-policy.general.table.enabled"),
          value: "enabled",
          sortable: false,
          width: "12%",
        },
        {
          text: "",
          value: "actions",
          sortable: false,
          align: "right",
          width: "14%",
        },
      ];
    },
    policyHeaders() {
      const columns = [
        {
          text: this.$t("wkt.admin.vacation-policy.table.name"),
          value: "name",
          type: "text",
        },
        {
          text: this.$t("wkt.admin.vacation-policy.table.assisted_count_mode"),
          value: "assistedCountMode",
          type: "mode",
        },
        {
          text: this.$t("wkt.admin.vacation-policy.table.days_vacation"),
          value: "yearlyVacationDays",
          type: "metric",
          unit: HOURS_BAG_UNIT_DAYS,
        },
      ];

      this.bagDefinitions
        .filter((definition) =>
          Boolean(this.company && this.company[definition.enabledProp]),
        )
        .forEach((definition) => {
          columns.push({
            text: definition.columnLabel,
            value: definition.policyProp,
            type: "metric",
            unit: this.getDefinitionUnit(definition),
          });
        });

      return columns;
    },
  },
  methods: {
    onReveal() {
      if (!gwt.getSessionInformation()) {
        setTimeout(() => this.onReveal(), 1000);
        return;
      }

      this.sessionProfile = gwt.getSessionProfile();
      this.loadInitialData();
    },
    refreshModule() {
      this.loadInitialData();
    },
    loadInitialData() {
      this.isLoading = true;
      this.reloadCompanyFromApi(() => {
        this.loadPolicies();
      });
    },
    reloadCompanyFromSession() {
      const sessionInformation = gwt.getSessionInformation();
      this.company = sessionInformation
        ? this.cloneData(sessionInformation.company)
        : null;
    },
    reloadCompanyFromApi(callback) {
      api.getCompany(
        (company) => {
          this.company = this.cloneData(company);
          this.updateSessionCompany();

          if (callback) {
            callback();
          }
        },
        (error) => {
          this.isLoading = false;
          this.doShowAlert({
            text: this.getCallbackErrorMessage(error),
            color: "error",
            timeout: 4000,
          });
        },
      );
    },
    cloneData(value) {
      return value ? deepClone(value) : null;
    },
    doShowAlert(payload) {
      this.alertText = this.st(payload.text || "");
      this.alertColor = payload.color;
      this.alertTimeout = payload.timeout;
      this.showAlert = true;
    },
    showErrorAlert(message) {
      this.doShowAlert({
        text: message || this.$t("wkt.admin.common.error.save_server"),
        color: "error",
        timeout: 4000,
      });
    },
    showSuccessAlert(message) {
      this.doShowAlert({
        text:
          message ||
          this.$t("wkt.admin.general-customize.data_saved_correctly"),
        color: "success",
        timeout: 3000,
      });
    },
    loadPolicies() {
      if (!this.company) {
        this.isLoading = false;
        return;
      }

      this.apiLoading = true;

      api.getVacationPolicies(
        this.search,
        (data) => {
          this.policies = Array.isArray(data) ? data : [];
          this.apiLoading = false;
          this.isLoading = false;
        },
        (error) => {
          this.apiLoading = false;
          this.isLoading = false;
          this.doShowAlert({
            text: this.getCallbackErrorMessage(error),
            color: "error",
            timeout: 4000,
          });
        },
      );
    },
    onSearchUpdated() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }

      this.searchTimer = setTimeout(() => {
        this.loadPolicies();
      }, 400);
    },
    handlePolicyRowClick(item) {
      this.openPolicyEditor(item.rawPolicy);
    },
    openDefaultDialog() {
      if (!this.company) {
        return;
      }

      this.$refs.defaultDialog.open(this.company, this.canModify, (result) => {
        if (!result || !result.success) {
          if (result) {
            this.showErrorAlert(result.message);
          }
          return;
        }

        this.company = this.cloneData(result.company);
        this.updateSessionCompany();
        this.showSuccessAlert(result.message);
      });
    },
    openBagDialog(definition) {
      if (!this.company) {
        return;
      }

      this.$refs.bagDialog.open(
        this.company,
        definition,
        this.canModify,
        (result) => {
          if (!result || !result.success) {
            if (result) {
              this.showErrorAlert(result.message);
            }
            return;
          }

          this.company = this.cloneData(result.company);
          this.updateSessionCompany();
          this.showSuccessAlert(result.message);
        },
        {
          licenseType: this.licenseType,
        },
      );
    },
    openAccrualDateDialog() {
      if (!this.company) {
        return;
      }

      this.$refs.accrualDialog.open(
        this.company,
        this.canModify,
        this.licenseType === LICENSE_TYPE_CORPORATE,
        (result) => {
          if (!result || !result.success) {
            if (result) {
              this.showErrorAlert(result.message);
            }
            return;
          }

          this.company = this.cloneData(result.company);
          this.updateSessionCompany();
          this.showSuccessAlert(result.message);
        },
      );
    },
    openPolicyEditor(policy = null) {
      if (!this.company) {
        return;
      }

      this.$refs.policyDialog.open(
        this.company,
        policy,
        this.policies,
        this.canModify,
        (result) => {
          if (!result) {
            return;
          }

          if (!result.success) {
            this.showErrorAlert(result.message);
            return;
          }

          this.loadPolicies();
          this.showSuccessAlert(result.message);
        },
      );
    },
    updateSessionCompany() {
      gwt.setSessionCompany(JSON.stringify(this.company));
    },
    getBagDisplayLabel(labelProp, fallbackKey) {
      if (this.company && this.company[labelProp]) {
        return this.company[labelProp];
      }

      return this.$t(fallbackKey);
    },
    getBagDefinitions() {
      return [
        {
          id: 7,
          rowLabel: this.getBagDisplayLabel(
            "defaultYearlyExtraLabel",
            "wkt.admin.vacation-policy.table.days_extra",
          ),
          columnLabel: this.getBagDisplayLabel(
            "defaultYearlyExtraLabel",
            "wkt.admin.vacation-policy.table.days_extra",
          ),
          labelProp: "defaultYearlyExtraLabel",
          daysProp: "defaultYearlyExtraDays",
          allowNegativeProp: "defaultYearlyExtraAllowNegative",
          enabledProp: "defaultYearlyExtraEnabled",
          unitProp: "defaultYearlyExtraUnit",
          policyProp: "yearlyExtraDays",
          minimumLicenseType: LICENSE_TYPE_FREE,
        },
        {
          id: 1,
          rowLabel: this.getBagDisplayLabel(
            "defaultYearlyHoursBag1Label",
            "wkt.admin.vacation-policy.table.bag1",
          ),
          columnLabel: this.getBagDisplayLabel(
            "defaultYearlyHoursBag1Label",
            "wkt.admin.vacation-policy.table.bag1",
          ),
          labelProp: "defaultYearlyHoursBag1Label",
          daysProp: "defaultYearlyHoursBag1Days",
          allowNegativeProp: "defaultYearlyHoursBag1AllowNegative",
          enabledProp: "defaultYearlyHoursBag1Enabled",
          unitProp: "defaultYearlyHoursBag1Unit",
          policyProp: "yearlyHoursBag1Days",
          minimumLicenseType: LICENSE_TYPE_BUSINESS,
        },
        {
          id: 2,
          rowLabel: this.getBagDisplayLabel(
            "defaultYearlyHoursBag2Label",
            "wkt.admin.vacation-policy.table.bag2",
          ),
          columnLabel: this.getBagDisplayLabel(
            "defaultYearlyHoursBag2Label",
            "wkt.admin.vacation-policy.table.bag2",
          ),
          labelProp: "defaultYearlyHoursBag2Label",
          daysProp: "defaultYearlyHoursBag2Days",
          allowNegativeProp: "defaultYearlyHoursBag2AllowNegative",
          enabledProp: "defaultYearlyHoursBag2Enabled",
          unitProp: "defaultYearlyHoursBag2Unit",
          policyProp: "yearlyHoursBag2Days",
          minimumLicenseType: LICENSE_TYPE_BUSINESS,
        },
        {
          id: 3,
          rowLabel: this.getBagDisplayLabel(
            "defaultYearlyHoursBag3Label",
            "wkt.admin.vacation-policy.table.bag3",
          ),
          columnLabel: this.getBagDisplayLabel(
            "defaultYearlyHoursBag3Label",
            "wkt.admin.vacation-policy.table.bag3",
          ),
          labelProp: "defaultYearlyHoursBag3Label",
          daysProp: "defaultYearlyHoursBag3Days",
          allowNegativeProp: "defaultYearlyHoursBag3AllowNegative",
          enabledProp: "defaultYearlyHoursBag3Enabled",
          unitProp: "defaultYearlyHoursBag3Unit",
          policyProp: "yearlyHoursBag3Days",
          minimumLicenseType: LICENSE_TYPE_CORPORATE,
        },
        {
          id: 4,
          rowLabel: this.getBagDisplayLabel(
            "defaultYearlyHoursBag4Label",
            "wkt.admin.vacation-policy.table.bag4",
          ),
          columnLabel: this.getBagDisplayLabel(
            "defaultYearlyHoursBag4Label",
            "wkt.admin.vacation-policy.table.bag4",
          ),
          labelProp: "defaultYearlyHoursBag4Label",
          daysProp: "defaultYearlyHoursBag4Days",
          allowNegativeProp: "defaultYearlyHoursBag4AllowNegative",
          enabledProp: "defaultYearlyHoursBag4Enabled",
          unitProp: "defaultYearlyHoursBag4Unit",
          policyProp: "yearlyHoursBag4Days",
          minimumLicenseType: LICENSE_TYPE_CORPORATE,
        },
        {
          id: 5,
          rowLabel: this.getBagDisplayLabel(
            "defaultYearlyHoursBag5Label",
            "wkt.admin.vacation-policy.table.bag5",
          ),
          columnLabel: this.getBagDisplayLabel(
            "defaultYearlyHoursBag5Label",
            "wkt.admin.vacation-policy.table.bag5",
          ),
          labelProp: "defaultYearlyHoursBag5Label",
          daysProp: "defaultYearlyHoursBag5Days",
          allowNegativeProp: "defaultYearlyHoursBag5AllowNegative",
          enabledProp: "defaultYearlyHoursBag5Enabled",
          unitProp: "defaultYearlyHoursBag5Unit",
          policyProp: "yearlyHoursBag5Days",
          minimumLicenseType: LICENSE_TYPE_CORPORATE,
        },
      ];
    },
    getDefinitionUnit(definition) {
      if (
        !this.company ||
        !definition.unitProp ||
        this.company[definition.unitProp] == null
      ) {
        return HOURS_BAG_UNIT_DAYS;
      }

      return Number(this.company[definition.unitProp]);
    },
    getBagOrderLabel(index) {
      return this.$i18n.locale === "en"
        ? `Bag ${index + 1}`
        : `Bolsa ${index + 1}`;
    },
    isBagAllowedByLicense(definition) {
      if (this.licenseType === LICENSE_TYPE_CORPORATE) {
        return true;
      }

      if (definition.minimumLicenseType === LICENSE_TYPE_FREE) {
        return true;
      }

      if (this.licenseType === LICENSE_TYPE_BUSINESS) {
        return definition.minimumLicenseType <= LICENSE_TYPE_BUSINESS;
      }

      return false;
    },
    isBagActionDisabled(definition) {
      return !this.canModify || !this.isBagAllowedByLicense(definition);
    },
    formatBagLabel(row) {
      return row.enabled && row.label
        ? row.label
        : this.$t("wkt.admin.vacation-policy.general.not_available");
    },
    formatBagUnit(row) {
      return row.enabled
        ? this.formatGeneralUnit(row.unit)
        : this.$t("wkt.admin.vacation-policy.general.not_available");
    },
    formatBagBoolean(enabled, value) {
      return enabled
        ? this.formatBoolean(value)
        : this.$t("wkt.admin.vacation-policy.general.not_available");
    },
    formatMetricValue(value) {
      const numericValue = Number(value || 0);
      return Number.isInteger(numericValue)
        ? numericValue
        : numericValue.toFixed(2);
    },
    formatBoolean(value) {
      return value
        ? this.$t("wkt.admin.common.yes")
        : this.$t("wkt.admin.common.no");
    },
    formatCountMode(mode) {
      switch (Number(mode || 0)) {
        case 1:
          return this.$t(
            "wkt.admin.vacation-policy.vacation-policy-window.count_mode_natural_days",
          );
        case 2:
          return this.$t(
            "wkt.admin.vacation-policy.vacation-policy-window.count_mode_none_work_days",
          );
        default:
          return this.$t(
            "wkt.admin.vacation-policy.vacation-policy-window.count_mode_none",
          );
      }
    },
    formatUnit(unit) {
      return Number(unit) === HOURS_BAG_UNIT_HOURS
        ? this.$t("wkt.admin.vacation-policy.unit.hours")
        : this.$t("wkt.admin.vacation-policy.unit.days");
    },
    formatGeneralUnit(unit) {
      return Number(unit) === HOURS_BAG_UNIT_HOURS
        ? this.$t("wkt.admin.vacation-policy.unit.hours_plural")
        : this.$t("wkt.admin.vacation-policy.unit.days_plural");
    },
    formatAccrualDate(simpleDate) {
      const today = new Date();
      const normalized = simpleDate || {
        day: 1,
        month: 1,
        year: today.getFullYear(),
      };

      const date = new Date(
        normalized.year,
        normalized.month - 1,
        normalized.day,
      );

      return new Intl.DateTimeFormat([this.$i18n.locale, "en"], {
        day: "numeric",
        month: "long",
      }).format(date);
    },
  },
};
</script>
